import { KeyboardEvent, MouseEvent, ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AccountSignIn, AccountSummary, AccountsSummary, SentInvite, createAccountApi, listAccounts,
} from '../../api/admin';
import { listPlansApi } from '../../api/platform';
import { ConsoleBillingFields, PlanSummary, atLeastPlatform } from '../../shared/types';
import {
  USAGE_WORD, isoPlusDays, localDay, money, ordinal, shortMD, shortMDY, todayIso,
} from './planDisplay';
import { FigureTile, Modal, TableCard } from '../../components/fs';
import { InviteLinkModal, invitedMessage } from '../../components/PasswordDialogs';
import { usePlatform } from './PlatformLayout';
import { AuditSection, describeAudit } from './platformAudit';
import { useToast } from '../../components/Toast';
import { PHONE, useMediaQuery } from '../../shared/media';

/**
 * An account row plus its billing mirror. Partial because the fields arrive
 * with the billing feature's server half — a row without them still renders,
 * as "no charge" rather than as a hole.
 */
type ConsoleRow = AccountSummary & Partial<ConsoleBillingFields>;

/**
 * The row's plan KEY. R12's server sends `plan` as the key string (it used
 * to overwrite it with `{ key, name }`, which is why `?plan=` never matched in
 * production); a row from an older server that still carries the object is
 * read by its `key` rather than compared as an object and silently dropped.
 */
function planKeyOf(a: ConsoleRow): string {
  const p = a.plan as unknown;
  if (typeof p === 'string') return p;
  const key = p && typeof p === 'object' ? (p as { key?: unknown }).key : undefined;
  return typeof key === 'string' ? key : '';
}

/** What the row's plan is called — the joined name, else its key. */
const planLabel = (a: ConsoleRow) => a.plan_name || planKeyOf(a);

/** One account's page (074) — every action on an account lives there. */
const accountPath = (id: number) => `/platform/accounts/${id}`;

// A new account's choices. The labels and intents below still know 'trial'
// and 'past_due', which HealthCell derives from billing (review finding 24).
const STATUSES = ['active', 'suspended', 'cancelled'] as const;
/** Account status -> pill intent. Caution = will change on its own; warn = needs a human. */
const STATUS_INTENT: Record<string, string> = {
  trial: 'caution', active: 'ok', past_due: 'warn', suspended: 'danger', cancelled: 'neutral',
};
const STATUS_LABEL: Record<string, string> = {
  trial: 'trial', active: 'active', past_due: 'past due', suspended: 'suspended', cancelled: 'cancelled',
};

/**
 * Which header the list is ordered by. `null` keeps the server's order — oldest
 * account first — so the list only rearranges once staff ask it to.
 */
type SortKey = 'name' | 'email' | 'health' | 'plan' | 'mrr' | 'last';
type Sort = { key: SortKey; dir: 'asc' | 'desc' };
const SORT_KEYS: SortKey[] = ['name', 'email', 'health', 'plan', 'mrr', 'last'];

/** `?sort=mrr.desc` — anything else is the server's order. */
function sortOf(raw: string | null): Sort | null {
  const [key, dir] = (raw || '').split('.');
  return SORT_KEYS.includes(key as SortKey) && (dir === 'asc' || dir === 'desc')
    ? { key: key as SortKey, dir } : null;
}

/** Health sorts by how loudly a row is asking for a human, not alphabetically. */
const HEALTH_RANK: Record<string, number> = {
  past_due: 5, suspended: 4, trial: 3, active: 2, cancelled: 1,
};

function sortValue(a: ConsoleRow, key: SortKey): string | number {
  switch (key) {
    case 'name': return a.name.toLowerCase();
    case 'email': return (a.billing_email || '').toLowerCase();
    case 'plan': return planLabel(a).toLowerCase();
    case 'mrr': return a.mrr_cents || 0;
    case 'last': return a.last_submission_at ? new Date(a.last_submission_at).getTime() : 0;
    case 'health': {
      // The same precedence HealthCell renders with: Stripe's word first,
      // the account's own status behind it.
      const s = a.subscription_status === 'past_due' ? 'past_due'
        : a.subscription_status === 'trialing' ? 'trial' : a.status;
      return HEALTH_RANK[s] ?? 0;
    }
  }
}

/** The only editor left on the list: a new account. Everything else is on the account page. */
type Editor = { kind: 'create' } | null;

/**
 * The service owner's customer list (R12, screen 37; the account page, 074):
 * the page head with the visible rows summed, the four figure tiles over every
 * account, the Customers table, and the recent platform trail.
 *
 * A row opens its account — the whole row, or its name from the keyboard —
 * and every action on an account lives there: support mode, the plan, logins,
 * status and deletion. One click deeper than the row menu that held them was,
 * which the handoff accepted for the page it bought. The list's search, sort
 * and plan filter live in the URL, so coming Back finds it as it was left.
 */
export default function PlatformConsole() {
  const { role } = usePlatform();
  const [accounts, setAccounts] = useState<ConsoleRow[] | null>(null);
  /** The tiles' figures, counted by the server over EVERY account; absent from an older server. */
  const [summary, setSummary] = useState<AccountsSummary | null>(null);
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  /** Stripe connected: a paid plan needs something billing it, so New account starts on Free (Ben, 2026-09-27) */
  const [stripeConnected, setStripeConnected] = useState(false);
  // The plans table's Accounts column links here with ?plan=team; the header sums what shows.
  const [params, setParams] = useSearchParams();
  const planFilter = params.get('plan');
  /** Typed search over the rows already in hand — no round trip, so no debounce. */
  const query = params.get('q') ?? '';
  const sort = sortOf(params.get('sort'));
  const [error, setError] = useState('');
  const [editor, setEditor] = useState<Editor>(null);
  /** a card per account instead of the 7-column table (§4I I2) */
  const phone = useMediaQuery(PHONE);
  /** Bumped by anything that writes, so the trail below reflects what you just did. */
  const [changed, setChanged] = useState(0);
  /** What just happened, when it is not visible in the table (an invitation). */
  const toast = useToast();
  /** set only when an invitation could not be emailed — see InviteLinkModal */
  const [handOver, setHandOver] = useState<SentInvite | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Support can look and masquerade; everything that provisions a customer or
  // touches their terms is Admin and above. The server enforces this — the UI
  // just avoids offering what would come back 403.
  const canManage = atLeastPlatform(role, 'admin');

  const load = () => listAccounts()
    .then((r) => { setAccounts(r.accounts); setSummary(r.summary ?? null); setChanged((n) => n + 1); })
    .catch((e) => setError(e.message));

  useEffect(() => {
    load();
    listPlansApi().then((r) => { setPlans(r.plans); setStripeConnected(!!r.stripe?.connected); }).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** One search param in or out of the URL, the others kept — `replace`, so typing is not history. */
  const setParam = (key: string, value: string | null) => setParams((cur) => {
    const next = new URLSearchParams(cur);
    if (value) next.set(key, value); else next.delete(key);
    return next;
  }, { replace: true });
  const setQuery = (q: string) => setParam('q', q || null);
  const setSort = (s: Sort) => setParam('sort', `${s.key}.${s.dir}`);

  /**
   * A write that ends in an invitation — New account's owner. When the link
   * could not be emailed it is handed over on screen, a dialog rather than a
   * line of green text.
   */
  async function writeInvite(fn: () => Promise<{ invite?: SentInvite | null }>) {
    setError('');
    try {
      const r = await fn();
      setEditor(null);
      if (r.invite && !r.invite.emailed) setHandOver(r.invite);
      else if (r.invite) toast(invitedMessage(r.invite));
      load();
    } catch (err: any) { setError(err.message); }
  }

  /** Where the account page's "← Accounts" returns to: this list, as it is now. */
  const cameFrom = { from: location.search };
  const openAccount = (a: ConsoleRow) => navigate(accountPath(a.id), { state: cameFrom });
  /**
   * The whole row opens its account — except a click that landed on something
   * with its own job (the name's link, the billing email's mailto).
   */
  const onRowClick = (e: MouseEvent, a: ConsoleRow) => {
    if ((e.target as HTMLElement).closest('a, button, input')) return;
    openAccount(a);
  };

  // The header sums these rows, so its number can be checked against the
  // table rather than taken on trust — including once a search has narrowed it.
  const needle = query.trim().toLowerCase();
  let visible = accounts === null ? null
    : planFilter ? accounts.filter((a) => planKeyOf(a) === planFilter) : accounts;
  if (visible && needle) {
    visible = visible.filter((a) => a.name.toLowerCase().includes(needle)
      || (a.billing_email || '').toLowerCase().includes(needle)
      || (a.owner_email || '').toLowerCase().includes(needle));
  }
  if (visible && sort) {
    const flip = sort.dir === 'asc' ? 1 : -1;
    visible = [...visible].sort((x, y) => {
      const a = sortValue(x, sort.key);
      const b = sortValue(y, sort.key);
      if (a === b) return x.name.localeCompare(y.name);
      const cmp = typeof a === 'string' ? a.localeCompare(b as string) : a < (b as number) ? -1 : 1;
      return cmp * flip;
    });
  }
  const filterName = planFilter
    ? (plans.find((p) => p.key === planFilter)?.name || planFilter)
    : null;

  return (
    <>
      <div className="pl-page pl-accounts">
        <div className="fs-page-head pl-head">
          <div className="text">
            <h1>Accounts</h1>
            <div className="fs-page-sub">
              {visible ? <HeadStrip accounts={visible} /> : 'Loading…'}
              {filterName && (
                <>
                  {' — only accounts on '}<b>{filterName}</b>{' '}
                  <a href="#" className="pl-acct-showall"
                    onClick={(e) => { e.preventDefault(); setParam('plan', null); }}>show all</a>
                </>
              )}
            </div>
          </div>
        </div>
        {error && <div className="ad-err">{error}</div>}

        {summary && <AccountTiles summary={summary} />}

        <TableCard
          className="pl-acct-table"
          title="Customers"
          action={(
            <div className="pl-acct-tools">
              <input
                className="fs-input pl-search"
                placeholder="Search name or billing email…"
                aria-label="Search accounts by name or billing email"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {canManage && (
                <button className="fs-btn primary" onClick={() => setEditor({ kind: 'create' })}>New account</button>
              )}
            </div>
          )}
        >
          {!visible ? <div className="st-card-body st-muted">Loading…</div> : visible.length === 0 ? (
            <div className="st-card-body st-muted st-empty">
              {needle ? `No account matches “${query.trim()}”.`
                : planFilter ? `No accounts on ${filterName}.` : 'No accounts yet.'}
            </div>
          ) : phone ? (
            // The design's phone list (screen 14): a card per account, the whole
            // card opening it — the name, its plan, and how it is doing.
            <div className="pl-acct-cards">
              {visible.map((a) => (
                <Link className="pl-acct" key={a.id} to={accountPath(a.id)} state={cameFrom}>
                  <div className="head">
                    <span className="name">{a.name}</span>
                    <span className="pl-acct-plan">{planLabel(a)} ›</span>
                  </div>
                  {/* How the tenant signs in is said nowhere else on a phone. */}
                  <div className="facts">{a.owner_email || 'no owner login'} · {signInWords(a.signIn)}</div>
                  <div className="pills"><HealthCell account={a} /></div>
                </Link>
              ))}
            </div>
          ) : (
            <table className="fs-table">
              <thead>
                <tr>
                  <SortHead sort={sort} onSort={setSort} col="name">Account</SortHead>
                  <SortHead sort={sort} onSort={setSort} col="email">Billing email</SortHead>
                  <SortHead sort={sort} onSort={setSort} col="health">Health</SortHead>
                  <SortHead sort={sort} onSort={setSort} col="plan">Plan</SortHead>
                  <SortHead sort={sort} onSort={setSort} col="mrr" numeric>MRR</SortHead>
                  <SortHead sort={sort} onSort={setSort} col="last">Last activity</SortHead>
                  <th className="fs-row-actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <tr key={a.id} className="pl-acct-row clickable" onClick={(e) => onRowClick(e, a)}>
                    <td>
                      {/* The row's keyboard way in: Tab to the name, Enter opens it. */}
                      <Link className="pl-acct-name pl-acct-open" to={accountPath(a.id)} state={cameFrom}>{a.name}</Link>
                      <div className="fs-cell-sub faint pl-acct-owner">
                        {a.owner_email || 'no owner login'} · {signInWords(a.signIn)}
                      </div>
                    </td>
                    <td>
                      {a.billing_email
                        ? <a className="pl-acct-mail" href={`mailto:${a.billing_email}`}>{a.billing_email}</a>
                        : <span className="st-faint">not set</span>}
                    </td>
                    <td><HealthCell account={a} /></td>
                    <td><PlanCell account={a} plans={plans} /></td>
                    <td className="num">
                      {a.mrr_cents ? money(a.mrr_cents) : <span className="st-faint">—</span>}
                    </td>
                    <td className="pl-acct-when">
                      {a.last_submission_at ? new Date(a.last_submission_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="fs-row-actions pl-acct-go" aria-hidden="true">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableCard>

        <AuditSection reloadKey={changed} onError={setError} />
      </div>

      {editor?.kind === 'create' && canManage && (
        <CreateAccountModal
          plans={plans}
          paidNeedsBilling={stripeConnected}
          onClose={() => setEditor(null)}
          onCreate={(body) => writeInvite(async () => {
            const r = await createAccountApi(body);
            return { invite: r.ownerInvite };
          })}
        />
      )}

      {handOver && <InviteLinkModal invite={handOver} onClose={() => setHandOver(null)} />}
    </>
  );
}

/**
 * A column header you can order the list by.
 *
 * First click takes the direction that reads as "most interesting first" for
 * that column — A–Z for names, biggest and most recent for money and dates —
 * and clicking the live column again reverses it.
 */
function SortHead({ col, sort, onSort, numeric, children }: {
  col: SortKey;
  sort: Sort | null;
  onSort: (s: Sort) => void;
  numeric?: boolean;
  children: ReactNode;
}) {
  const on = sort?.key === col;
  const firstDir: 'asc' | 'desc' = col === 'name' || col === 'email' || col === 'plan' ? 'asc' : 'desc';
  const flip = () => onSort({ key: col, dir: on && sort!.dir === firstDir ? (firstDir === 'asc' ? 'desc' : 'asc') : firstDir });
  // Reachable from the keyboard too: Tab to the header, Enter or Space sorts.
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flip(); }
  };
  return (
    <th
      className={`sortable${numeric ? ' num' : ''}`}
      aria-sort={on ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      tabIndex={0}
      onClick={flip}
      onKeyDown={onKey}
    >
      {children}
      <span className="fs-sort-caret">{on ? (sort!.dir === 'asc' ? '↑' : '↓') : ''}</span>
    </th>
  );
}

/**
 * The four figures over EVERY account (R12, screen 37) — the server's
 * `summary`, not the visible rows, which the page's sub-line sums instead.
 * MRR is after the agreed price and a running percent-off; a trial is a
 * standing free period or a signup trial; past due is Stripe's word or the
 * account's own status.
 */
function AccountTiles({ summary: s }: { summary: AccountsSummary }) {
  return (
    <div className="pl-tiles pl-acct-tiles">
      <FigureTile label="MRR" value={money(s.mrrCents)} caption={`${s.paying} paying`} />
      <FigureTile label="Paying" value={String(s.paying)} caption={`of ${s.accounts}`} />
      <FigureTile
        label="Trials" value={String(s.trials)}
        caption={`${s.trialsEndingSoon} ending this week`}
        tone={s.trialsEndingSoon > 0 ? 'warn' : undefined}
      />
      <FigureTile
        label="Past due" value={String(s.pastDue)}
        caption={s.pastDue > 0 ? 'needs a nudge' : 'nobody'}
        tone={s.pastDue > 0 ? 'danger' : 'muted'}
      />
    </div>
  );
}

/**
 * The header strip: what the visible rows are worth a month, and who is on a
 * free upgrade period. Summed client-side from the same rows the table shows,
 * so the number can be checked against the table rather than taken on trust.
 */
function HeadStrip({ accounts }: { accounts: ConsoleRow[] }) {
  let cents = 0;
  let paying = 0;
  let trialling = 0;
  for (const a of accounts) {
    cents += a.mrr_cents || 0;
    if (a.mrr_cents) paying += 1;
    if (a.trial) trialling += 1;
  }
  return (
    <span className="pl-num">
      {accounts.length} account{accounts.length === 1 ? '' : 's'} · MRR <b>{money(cents)}</b> · {paying} paying · {trialling} trialling
    </span>
  );
}

/** The Account sub-line: how this tenant signs in, in five words or fewer. */
function signInWords(signIn?: AccountSignIn): string {
  if (!signIn || signIn.mode === 'password') return 'passwords only';
  const mode = signIn.mode === 'required' ? 'SSO required' : 'SSO optional';
  return signIn.providerName ? `${mode} · ${signIn.providerName}` : mode;
}

/**
 * Status pill + billing pill side by side, and the single most useful sub-line:
 * a failing provider, a failed charge, or the closest-to-its-ceiling metric —
 * so a row goes loud before the customer calls. An account waiting to be
 * deleted (074) says only that, and when.
 */
function HealthCell({ account: a }: { account: ConsoleRow }) {
  const pills: { intent: string; text: string }[] = [];
  const sub = a.subscription_status;

  if (a.delete_after) {
    pills.push({ intent: 'danger', text: `deleting · ${shortMD(localDay(a.delete_after))}` });
  } else if (sub === 'trialing') {
    pills.push({ intent: 'caution', text: a.trial ? `trial · ${a.trial.days_left}d left` : 'trial' });
  } else if (sub === 'past_due') {
    // The declined card's grace (Ben, 2026-09-27): the day it moves to Free.
    pills.push({ intent: 'warn', text: a.grace_until ? `past due · Free ${shortMD(localDay(a.grace_until))}` : 'past due' });
  } else {
    pills.push({ intent: STATUS_INTENT[a.status] || 'neutral', text: STATUS_LABEL[a.status] || a.status });
    if (sub === 'active') pills.push({ intent: 'ok', text: 'paid' });
    // canceled · incomplete — mirrored verbatim, never locally inferred.
    else if (sub) pills.push({ intent: 'neutral', text: sub });
  }

  const parts: { text: string; tone: '' | 'bad' | 'warn' }[] = [];
  if (a.signIn?.failingProvider) {
    parts.push({ text: `${a.signIn.failingProvider} checks failing`, tone: 'bad' });
  }
  if ((a.failed_attempts || 0) > 0) {
    parts.push({ text: `${ordinal(a.failed_attempts!)} attempt failed`, tone: 'bad' });
  }
  const u = a.top_usage;
  if (u && u.allowed !== null && !a.signIn?.failingProvider) {
    const pct = Math.max(0, Math.round(u.pct));
    if (pct >= 100) parts.push({ text: `at ${USAGE_WORD[u.limit]} limit`, tone: 'bad' });
    else parts.push({ text: `${pct}% of ${USAGE_WORD[u.limit]}`, tone: pct >= 80 ? 'warn' : '' });
  }
  const tone = parts.some((p) => p.tone === 'bad') ? 'bad' : parts.some((p) => p.tone === 'warn') ? 'warn' : '';

  return (
    <div>
      <div className="pl-health-pills">
        {pills.map((p, i) => <span key={i} className={`fs-pill ${p.intent}`}>{p.text}</span>)}
      </div>
      {parts.length > 0 && (
        <div className={`fs-cell-sub${tone ? ` ${tone}` : ''}`}>{parts.map((p) => p.text).join(' · ')}</div>
      )}
    </div>
  );
}

/**
 * The plan a customer is on: what they pay for, then what it costs and when it
 * renews. While a free upgrade period is active the sub-line names the trialled
 * plan — the name is what they pay for, the line is what they are holding.
 */
function PlanCell({ account: a, plans }: { account: ConsoleRow; plans: PlanSummary[] }) {
  const trialPlan = a.trial ? plans.find((p) => p.key === a.trial!.plan_key) : undefined;
  return (
    <div>
      <div>
        {planLabel(a)}
        {a.trial && <span className="pl-acct-trial"> → {a.trial.plan_name}</span>}
      </div>
      {a.subscription_status === 'trialing' && trialPlan?.price_monthly_cents != null ? (
        <div className="fs-cell-sub">
          auto-converts {money(trialPlan.price_monthly_cents, trialPlan.currency)}/mo
          {a.trial ? ` · ${shortMD(isoPlusDays(todayIso(), a.trial.days_left))}` : ''}
        </div>
      ) : a.trial ? (
        <div className="fs-cell-sub pl-acct-free">
          free {a.trial.plan_name} until {shortMD(isoPlusDays(todayIso(), a.trial.days_left))}
        </div>
      ) : a.subscription_status ? (
        <div className="fs-cell-sub pl-num">
          {a.billing_interval === 'year' ? 'annual' : 'monthly'}
          {a.current_period_end ? ` · renews ${shortMDY(a.current_period_end)}` : ''}
        </div>
      ) : (
        <div className="fs-cell-sub">no charge</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New account — the one editor the list keeps. The row menu's modals (add a
// user, logins, status, delete) moved to the account page (074).
// ---------------------------------------------------------------------------

/** A plan with a price — one that must be billed (the server's isPaidPlan, lib/billing.ts). */
const isPaidPlan = (p: PlanSummary) =>
  p.key !== 'free' && ((p.price_monthly_cents ?? 0) > 0 || (p.price_annual_cents ?? 0) > 0);

function CreateAccountModal({ plans, paidNeedsBilling, onClose, onCreate }: {
  plans: PlanSummary[];
  /** Stripe is connected, so a paid plan must be billed — it starts from the account's Change plan instead */
  paidNeedsBilling: boolean;
  onClose: () => void;
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({ name: '', planId: 0, status: 'active', ownerEmail: '', ownerName: '' });
  const submit = () => {
    if (!form.name.trim()) return;
    onCreate({ ...form, name: form.name.trim(), planId: form.planId || undefined });
  };
  return (
    <Modal title="New account" onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={!form.name.trim()} onClick={submit}>Create account</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-field">
        <label htmlFor="pl-new-name">Company name</label>
        <input id="pl-new-name" value={form.name} autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="fs-row-2 pl-modal-row">
        <div className="fs-field">
          <label htmlFor="pl-new-plan">Plan</label>
          <select id="pl-new-plan" value={form.planId} onChange={(e) => setForm({ ...form, planId: Number(e.target.value) })}>
            <option value={0}>Free</option>
            {plans.filter((p) => p.key !== 'free' && !p.retired_at).map((p) => (
              <option key={p.id} value={p.id} disabled={paidNeedsBilling && isPaidPlan(p)}>{p.name}</option>
            ))}
          </select>
          {paidNeedsBilling && (
            <span className="hint">A new account starts on Free. A paid plan comes from its Change plan — a free period that converts — or their own checkout.</span>
          )}
        </div>
        <div className="fs-field">
          <label htmlFor="pl-new-status">Status</label>
          <select id="pl-new-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
      </div>
      <div className="fs-row-2 pl-modal-row">
        <div className="fs-field">
          <label htmlFor="pl-new-owner-email">Owner email <span className="pl-optional">(optional)</span></label>
          <input id="pl-new-owner-email" type="email" value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })} />
          <span className="hint">Left blank, the account starts with no login.</span>
        </div>
        <div className="fs-field">
          <label htmlFor="pl-new-owner-name">Owner name <span className="pl-optional">(optional)</span></label>
          <input id="pl-new-owner-name" value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} />
        </div>
      </div>
      <div className="fs-consequence pl-modal-note">
        The owner is emailed a link and chooses their own password. You never see it, and there is
        nothing for you to read out to them.
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// The audit trail moved to platformAudit.tsx (R12): the whole log is its own
// page now, and the Accounts page keeps "Recent platform activity" from there.
// `describeAudit` is re-exported so what imported it here still finds it.
// ---------------------------------------------------------------------------
export { describeAudit };
